import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'wouter';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { 
  ArrowLeft,
  ChevronLeft, 
  ChevronRight, 
  CheckCircle2, 
  ExternalLink, 
  FileText, 
  Info,
  Maximize, 
  Minimize,
  Play, 
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

// Key takeaway component for highlighting important points
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="relative bg-gradient-to-r from-green-50 via-blue-50 to-slate-50 border border-green-200 rounded-lg p-3 my-4 overflow-hidden">
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-green-500 to-blue-600"></div>
      <div className="flex items-start">
        <div className="flex-shrink-0 mt-0.5">
          <div className="w-5 h-5 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center">
            <CheckCircle2 className="h-2.5 w-2.5 text-white" />
          </div>
        </div>
        <div className="ml-3 text-xs text-slate-700">{children}</div>
      </div>
    </div>
  );
};

// SVG Diagrams for the presentation

// Slide 2: Paradigm Shift Diagram
const ParadigmShiftDiagram = () => (
  <svg viewBox="0 0 800 300" className="w-full h-auto max-w-2xl mx-auto">
    {/* Reactive Medicine */}
    <rect x="50" y="100" width="200" height="100" rx="10" fill="#fee2e2" stroke="#ef4444" strokeWidth="2"/>
    <text x="150" y="130" textAnchor="middle" className="fill-red-800 text-sm font-semibold">
      Reactive Medicine
    </text>
    <text x="150" y="150" textAnchor="middle" className="fill-red-700 text-xs">
      Disease Treatment
    </text>
    <text x="150" y="170" textAnchor="middle" className="fill-red-600 text-xs">
      Wait for symptoms
    </text>
    
    {/* Arrow */}
    <path d="M 270 150 L 330 150" stroke="#374151" strokeWidth="3" markerEnd="url(#arrowhead)"/>
    <text x="300" y="140" textAnchor="middle" className="fill-gray-700 text-xs font-semibold">
      SHIFT TO
    </text>
    
    {/* Proactive Medicine */}
    <rect x="350" y="100" width="200" height="100" rx="10" fill="#dcfce7" stroke="#22c55e" strokeWidth="2"/>
    <text x="450" y="130" textAnchor="middle" className="fill-green-800 text-sm font-semibold">
      Longevity Medicine
    </text>
    <text x="450" y="150" textAnchor="middle" className="fill-green-700 text-xs">
      Healthspan Optimization
    </text>
    <text x="450" y="170" textAnchor="middle" className="fill-green-600 text-xs">
      Prevent & enhance
    </text>
    
    {/* Disease icons */}
    <g transform="translate(50, 220)">
      <circle cx="30" cy="20" r="15" fill="#fca5a5"/>
      <text x="30" y="25" textAnchor="middle" className="fill-red-800 text-xs">CVD</text>
      <circle cx="80" cy="20" r="15" fill="#fca5a5"/>
      <text x="80" y="25" textAnchor="middle" className="fill-red-800 text-xs">T2D</text>
      <circle cx="130" cy="20" r="15" fill="#fca5a5"/>
      <text x="130" y="25" textAnchor="middle" className="fill-red-800 text-xs">Cancer</text>
      <circle cx="180" cy="20" r="15" fill="#fca5a5"/>
      <text x="180" y="25" textAnchor="middle" className="fill-red-800 text-xs">Neuro</text>
    </g>
    
    {/* Arrow marker definition */}
    <defs>
      <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#374151"/>
      </marker>
    </defs>
  </svg>
);

// SHIFT Framework Pentagon
const SHIFTFrameworkDiagram = () => (
  <svg viewBox="0 0 600 600" className="w-full h-auto max-w-2xl mx-auto">
    {/* Pentagon shape */}
    <polygon 
      points="300,80 480,200 420,380 180,380 120,200" 
      fill="#f8fafc" 
      stroke="#475569" 
      strokeWidth="3"
    />
    
    {/* Central circle */}
    <circle cx="300" cy="250" r="60" fill="#e0e7ff" stroke="#4f46e5" strokeWidth="2"/>
    <text x="300" y="240" textAnchor="middle" className="fill-indigo-800 text-xs font-semibold">
      Lifestyle
    </text>
    <text x="300" y="255" textAnchor="middle" className="fill-indigo-800 text-xs font-semibold">
      Interventions
    </text>
    
    {/* SHIFT pillars */}
    {/* S - Sensing */}
    <circle cx="300" cy="120" r="40" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2"/>
    <text x="300" y="115" textAnchor="middle" className="fill-amber-800 text-xs font-bold">S</text>
    <text x="300" y="130" textAnchor="middle" className="fill-amber-700 text-xs">Sensing</text>
    
    {/* H - Homeostatic */}
    <circle cx="450" cy="180" r="40" fill="#dcfce7" stroke="#22c55e" strokeWidth="2"/>
    <text x="450" y="175" textAnchor="middle" className="fill-green-800 text-xs font-bold">H</text>
    <text x="450" y="190" textAnchor="middle" className="fill-green-700 text-xs">Defence</text>
    
    {/* I - Inflammation */}
    <circle cx="400" cy="340" r="40" fill="#fee2e2" stroke="#ef4444" strokeWidth="2"/>
    <text x="400" y="335" textAnchor="middle" className="fill-red-800 text-xs font-bold">I</text>
    <text x="400" y="350" textAnchor="middle" className="fill-red-700 text-xs">Inflammation</text>
    
    {/* F - Fibrosis */}
    <circle cx="200" cy="340" r="40" fill="#f3e8ff" stroke="#8b5cf6" strokeWidth="2"/>
    <text x="200" y="335" textAnchor="middle" className="fill-purple-800 text-xs font-bold">F</text>
    <text x="200" y="350" textAnchor="middle" className="fill-purple-700 text-xs">Fibrosis</text>
    
    {/* T - Timekeeping */}
    <circle cx="150" cy="180" r="40" fill="#e0f2fe" stroke="#0ea5e9" strokeWidth="2"/>
    <text x="150" y="175" textAnchor="middle" className="fill-sky-800 text-xs font-bold">T</text>
    <text x="150" y="190" textAnchor="middle" className="fill-sky-700 text-xs">Timekeeping</text>
  </svg>
);

// Define the slides array properly
const slides = [
  // Slide 1: Title Slide
  (
    <div className="space-y-8">
      <div className="text-center space-y-6">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Lifestyle Foundations 2.0
        </h1>
        <h2 className="text-2xl text-blue-600 mb-2">
          The SHIFT Framework for Longevity Medicine
        </h2>
        <h3 className="text-lg text-gray-600 mb-8">
          A Mechanistic Approach to Nutrition, Exercise & Sleep
        </h3>
        
        <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-lg">
          <div className="grid md:grid-cols-2 gap-6 text-left">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Presentation Details:</h4>
              <p className="text-sm text-gray-700">Duration: ~30 minutes</p>
              <p className="text-sm text-gray-700">Target: Physicians new to longevity medicine</p>
              <p className="text-sm text-gray-700">Focus: Practical application of lifestyle interventions</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Learning Objectives:</h4>
              <ul className="text-xs text-gray-700 space-y-1">
                <li>• Understand the SHIFT framework for aging</li>
                <li>• Apply mechanistic insights to patient care</li>
                <li>• Integrate lifestyle medicine into practice</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  ),

  // Slide 2: Paradigm Shift
  (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">
        The Paradigm Shift: From Reactive to Proactive Medicine
      </h1>
      
      <div className="flex justify-center mb-6">
        <ParadigmShiftDiagram />
      </div>
      
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <h3 className="font-semibold text-red-800 mb-2">Traditional Medicine</h3>
            <ul className="text-sm text-red-700 space-y-1">
              <li>• Wait for disease manifestation</li>
              <li>• Treat symptoms and pathology</li>
              <li>• Focus on single organ systems</li>
              <li>• Reactive interventions</li>
            </ul>
          </div>
        </div>
        
        <div className="space-y-4">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <h3 className="font-semibold text-green-800 mb-2">Longevity Medicine</h3>
            <ul className="text-sm text-green-700 space-y-1">
              <li>• Optimize healthspan before disease</li>
              <li>• Target root causes of aging</li>
              <li>• Systems-based approach</li>
              <li>• Proactive, personalized interventions</li>
            </ul>
          </div>
        </div>
      </div>
      
      <KeyTakeaway>
        Longevity medicine shifts focus from treating disease to optimizing the fundamental processes that maintain health and prevent age-related decline.
      </KeyTakeaway>
    </div>
  ),

  // Slide 3: SHIFT Framework Introduction
  (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          The SHIFT Framework
        </h1>
        <h2 className="text-xl text-gray-600">
          A Mechanistic Approach to Lifestyle Medicine
        </h2>
      </div>
      
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">The Five Pillars:</h3>
          
          <div className="space-y-3">
            <div className="flex items-center space-x-3 p-3 bg-amber-50 rounded-lg">
              <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold">S</div>
              <div>
                <h4 className="font-semibold text-amber-800">Sensing</h4>
                <p className="text-xs text-amber-700">Nutrient & energy detection (IIS, mTOR, AMPK, Sirtuins)</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">H</div>
              <div>
                <h4 className="font-semibold text-green-800">Homeostatic Defence</h4>
                <p className="text-xs text-green-700">Cellular maintenance (NRF2, p53/p16, Autophagy)</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg">
              <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white font-bold">I</div>
              <div>
                <h4 className="font-semibold text-red-800">Inflammation</h4>
                <p className="text-xs text-red-700">Immune response (NF-κB, JAK-STAT, SASP)</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg">
              <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold">F</div>
              <div>
                <h4 className="font-semibold text-purple-800">Fibrosis</h4>
                <p className="text-xs text-purple-700">Tissue remodeling (TGF-β, WNT, ECM)</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-3 bg-sky-50 rounded-lg">
              <div className="w-8 h-8 bg-sky-500 rounded-full flex items-center justify-center text-white font-bold">T</div>
              <div>
                <h4 className="font-semibold text-sky-800">Timekeeping</h4>
                <p className="text-xs text-sky-700">Biological rhythms (CLOCK/BMAL1, Epigenetic Clocks)</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-center">
          <SHIFTFrameworkDiagram />
        </div>
      </div>
      
      <KeyTakeaway>
        The SHIFT framework provides a mechanistic lens to understand how lifestyle interventions impact the fundamental processes of aging.
      </KeyTakeaway>
    </div>
  ),

  // Slide 4: Clinical Application
  (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">
        Applying SHIFT in Clinical Practice
      </h1>
      
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div className="p-6 bg-indigo-50 border border-indigo-200 rounded-lg">
            <h3 className="text-lg font-semibold text-indigo-900 mb-3">
              Core Principles
            </h3>
            <div className="space-y-2 text-sm text-indigo-800">
              <p>• Longevity medicine is proactive and healthspan-focused</p>
              <p>• <strong>Nutrition, Exercise, and Sleep are powerful, modifiable levers</strong></p>
              <p>• The SHIFT framework provides mechanistic understanding</p>
            </div>
          </div>
          
          <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
            <h3 className="text-lg font-semibold text-green-900 mb-3">
              Prioritize Foundational Habits
            </h3>
            <div className="space-y-3 text-sm text-green-800">
              <div className="flex items-start space-x-2">
                <span className="font-semibold">Diet:</span>
                <span>Whole foods, plant-rich, consider meal timing/fasting</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="font-semibold">Exercise:</span>
                <span>Balanced aerobic, resistance, and NEAT</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="font-semibold">Sleep:</span>
                <span>Consistent 7-9 hours, good hygiene, manage light</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="space-y-6">
          <div className="p-6 bg-purple-50 border border-purple-200 rounded-lg">
            <h3 className="text-lg font-semibold text-purple-900 mb-3">
              Clinical Implementation
            </h3>
            <div className="space-y-2 text-sm text-purple-800">
              <p>• Personalize recommendations based on individual assessments</p>
              <p>• Use biomarkers and goals for monitoring</p>
              <p>• Start with foundational interventions before advanced protocols</p>
            </div>
          </div>
          
          <div className="p-6 bg-amber-50 border border-amber-200 rounded-lg">
            <h3 className="text-lg font-semibold text-amber-900 mb-3">
              Long-term Perspective
            </h3>
            <p className="text-sm text-amber-800">
              Small, consistent changes can have profound long-term impacts on healthspan. Focus on sustainable interventions that patients can maintain.
            </p>
          </div>
        </div>
      </div>
      
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-6 rounded-lg">
        <h3 className="text-xl font-semibold text-indigo-900 mb-3 text-center">
          The SHIFT Framework in Practice
        </h3>
        <p className="text-sm text-indigo-800 text-center">
          Use SHIFT as a lens to understand and explain to patients how their lifestyle choices impact the fundamental processes of aging. This mechanistic understanding enhances compliance and empowers informed decision-making.
        </p>
      </div>
    </div>
  ),

  // Slide 5: Thank You
  (
    <div className="space-y-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">
        Thank You & Discussion
      </h1>
      
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div className="p-6 bg-indigo-50 border border-indigo-200 rounded-lg">
            <h3 className="text-lg font-semibold text-indigo-900 mb-3">
              Key Takeaways
            </h3>
            <ul className="space-y-2 text-sm text-indigo-800">
              <li>• The SHIFT framework provides mechanistic understanding</li>
              <li>• Lifestyle interventions are powerful, evidence-based tools</li>
              <li>• Start with foundations: nutrition, exercise, sleep</li>
              <li>• Personalize based on individual assessments</li>
            </ul>
          </div>
          
          <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
            <h3 className="text-lg font-semibold text-green-900 mb-3">
              Next Steps
            </h3>
            <p className="text-sm text-green-800">
              Begin implementing these concepts in your practice. Focus on sustainable interventions that patients can maintain long-term.
            </p>
          </div>
        </div>
        
        <div className="space-y-6">
          <div className="p-6 bg-purple-50 border border-purple-200 rounded-lg">
            <h3 className="text-lg font-semibold text-purple-900 mb-3">
              Questions & Discussion
            </h3>
            <p className="text-sm text-purple-800 mb-4">
              Thank you for your attention. Let's discuss how you can implement these concepts in your practice.
            </p>
            <div className="text-center">
              <div className="inline-flex items-center space-x-2 text-gray-600">
                <FileText className="h-5 w-5" />
                <span className="text-sm">Presentation Duration: ~30 minutes</span>
              </div>
            </div>
          </div>
          
          <div className="p-6 bg-gray-50 border border-gray-200 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Contact Information
            </h3>
            <p className="text-sm text-gray-700">
              For questions about this presentation or the SHIFT framework, please reach out through the mentorship platform.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
];

const LifestyleFoundations2: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const slideContainerRef = useRef<HTMLDivElement>(null);

  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      slideContainerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const previousSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <MentorshipAccessGate>
      <div className="flex flex-col h-screen">
        <Navbar />
        
        <div className="flex-grow overflow-hidden">
          <div className="flex flex-col h-full">
            {/* Header with navigation */}
            <div className="flex items-center justify-between p-4 bg-white border-b border-gray-200">
              <div className="flex items-center space-x-4">
                <Link href="/mentorship">
                  <Button variant="ghost" size="sm">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Mentorship
                  </Button>
                </Link>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900">
                    Lifestyle Foundations 2.0: The SHIFT Framework
                  </h1>
                  <p className="text-sm text-gray-600">
                    Slide {currentSlide + 1} of {slides.length}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm" onClick={handleFullscreen}>
                  {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Slide content */}
            <div className="flex-grow overflow-hidden">
              <div 
                ref={slideContainerRef}
                className="h-full flex flex-col"
              >
                <div className="flex-grow overflow-auto bg-white">
                  <div className="max-w-6xl mx-auto p-8">
                    {slides[currentSlide]}
                  </div>
                </div>

                {/* Navigation controls */}
                <div className="bg-white border-t border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <Button 
                      variant="outline" 
                      onClick={previousSlide}
                      disabled={currentSlide === 0}
                    >
                      <ChevronLeft className="h-4 w-4 mr-2" />
                      Previous
                    </Button>

                    {/* Slide indicators */}
                    <div className="flex space-x-2">
                      {slides.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => goToSlide(index)}
                          className={`w-3 h-3 rounded-full transition-colors ${
                            index === currentSlide 
                              ? 'bg-blue-600' 
                              : 'bg-gray-300 hover:bg-gray-400'
                          }`}
                        />
                      ))}
                    </div>

                    <Button 
                      variant="outline" 
                      onClick={nextSlide}
                      disabled={currentSlide === slides.length - 1}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    </MentorshipAccessGate>
  );
};

export default LifestyleFoundations2; 